import { useEffect, useRef, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { anuncioDaMudanca, intervaloDaFila, type TrabalhoDaFila } from "./fila.js";
import { mensagemDoErro } from "./impressora.js";

/**
 * A fila de impressão, como a tela a consulta.
 *
 * O worker não avisa por socket: a tela pergunta, de três em três segundos, e só enquanto há trabalho
 * aberto (`intervaloDaFila`). O painel de imprimir e a seção da página usam a **mesma** consulta, então
 * o React Query a deduplica — dois componentes, uma pergunta a cada três segundos.
 */
export function useTrabalhosDeImpressao() {
  return trpc.impressao.trabalhos.useQuery(undefined, {
    // Uma fila velha é pior que nenhuma: quem volta à página precisa ver o que aconteceu enquanto isso.
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (consulta) => intervaloDaFila(consulta.state.data),
  });
}

/** O relógio da tela, para a hora relativa ("há 3 min") não parar no instante em que foi desenhada. */
export function useAgora(intervaloMs = 30_000): Date {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const relogio = setInterval(() => setAgora(new Date()), intervaloMs);
    return () => clearInterval(relogio);
  }, [intervaloMs]);
  return agora;
}

/**
 * A frase para o `aria-live`, só quando a fila muda. A primeira leitura não anuncia nada: o histórico
 * que já estava lá não é novidade, e lê-lo inteiro ao abrir a página seria ruído.
 *
 * Com `apenasId`, só o trabalho dele — é o painel que acabou de enviar uma folha, que acompanha aquela.
 */
export function useAnuncioDaFila(
  trabalhos: readonly TrabalhoDaFila[] | undefined,
  apenasId?: number,
): string {
  const anterior = useRef<readonly TrabalhoDaFila[] | undefined>(undefined);
  const [anuncio, setAnuncio] = useState("");

  useEffect(() => {
    if (!trabalhos) return;
    const agora = apenasId === undefined ? trabalhos : trabalhos.filter((t) => t.id === apenasId);
    const frase = anterior.current ? anuncioDaMudanca(anterior.current, agora) : null;
    anterior.current = agora;
    if (frase) setAnuncio(frase);
  }, [trabalhos, apenasId]);

  return anuncio;
}

/** Cancelar um trabalho, com o erro (o servidor recusa o que já terminou) preso ao trabalho que o causou. */
export function useCancelarTrabalho() {
  const utils = trpc.useUtils();
  const [erro, setErro] = useState<{ id: number; mensagem: string } | null>(null);

  const cancelar = trpc.impressao.cancelar.useMutation({
    onError: (e, { id }) =>
      setErro({
        id,
        mensagem: mensagemDoErro(e, "Não foi possível cancelar. Tente de novo."),
      }),
    // Dar certo ou errado, o estado mudou: a fila de verdade é a do servidor. O CONFLICT, por exemplo, é
    // "já terminou" — e a linha precisa passar a dizer isso.
    onSettled: () => void utils.impressao.trabalhos.invalidate(),
  });

  return {
    cancelar: (id: number) => {
      setErro(null);
      cancelar.mutate({ id });
    },
    cancelandoId: cancelar.isPending ? (cancelar.variables?.id ?? null) : null,
    erro,
  };
}
